import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from "@angular/core";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { TagDTO } from "@core/models";
import { TagService } from "@core/services";
import { Subject, of } from "rxjs";
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-driveb-tag-popover",
  templateUrl: "./tag-popover.component.html",
  styleUrls: ["./tag-popover.component.scss"],
  standalone: false
})
export class TagPopoverComponent implements OnDestroy {
  @ViewChild("internalTagInput") internalTagInput?: ElementRef<HTMLInputElement>;
  @ViewChild("publicTagInput") publicTagInput?: ElementRef<HTMLInputElement>;
  @Input() tags: TagDTO[] = [];
  @Output() removeTag = new EventEmitter<TagDTO>();
  @Output() manageTags = new EventEmitter<void>();
  @Output() addTag = new EventEmitter<TagDTO>();

  isPopoverOpen = false;
  activeTagType?: TagDTO["tagType"];
  newTagName = "";
  suggestions: TagDTO[] = [];
  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top"
    },
    {
      originX: "end",
      originY: "bottom",
      overlayX: "end",
      overlayY: "top"
    },
    {
      originX: "end",
      originY: "top",
      overlayX: "end",
      overlayY: "bottom"
    }
  ];
  private closeTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(private tagService: TagService) {
    this.search$
      .pipe(
        debounceTime(300),
        map((name) => name.trim()),
        distinctUntilChanged(),
        switchMap((name) => {
          if (!this.activeTagType || !name) {
            return of([]);
          }

          return this.tagService
            .getFilteredTags(name, this.activeTagType)
            .pipe(map((response) => this.mapSuggestions(response)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (tags) => (this.suggestions = tags),
        error: () => (this.suggestions = [])
      });
  }

  ngOnDestroy(): void {
    this.clearCloseTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTriggerMouseEnter(): void {
    this.clearCloseTimeout();
    this.isPopoverOpen = true;
  }

  onTriggerMouseLeave(): void {
    if (this.activeTagType) {
      return;
    }

    this.scheduleClose();
  }

  onPopoverMouseEnter(): void {
    this.clearCloseTimeout();
  }

  onPopoverMouseLeave(): void {
    if (this.activeTagType) {
      return;
    }

    this.scheduleClose();
  }

  onTriggerClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isPopoverOpen = true;
  }

  onRemoveTag(tag: TagDTO): void {
    this.removeTag.emit(tag);
  }

  onStartAddTag(tagType: TagDTO["tagType"]): void {
    this.activeTagType = tagType;
    this.newTagName = "";
    this.suggestions = [];
    setTimeout(() => {
      const input =
        tagType === "INTERNAL" ? this.internalTagInput : this.publicTagInput;
      input?.nativeElement.focus();
    });
  }

  onTagInput(name: string): void {
    this.newTagName = name;
    this.search$.next(name);
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.emitNewTag();
    }

    if (event.key === "Escape") {
      this.cancelAddTag();
    }
  }

  onSuggestionSelected(tag: TagDTO): void {
    this.emitTag(tag.name, tag.tagType);
  }

  cancelAddTag(): void {
    this.activeTagType = undefined;
    this.newTagName = "";
    this.suggestions = [];
  }

  private emitNewTag(): void {
    if (!this.activeTagType) {
      return;
    }

    this.emitTag(this.newTagName, this.activeTagType);
  }

  private emitTag(name: string, tagType: TagDTO["tagType"]): void {
    const normalizedName = name.trim();
    const alreadyExists = this.tags.some(
      (tag) =>
        tag.tagType === tagType &&
        tag.name.trim().toLocaleLowerCase() === normalizedName.toLocaleLowerCase()
    );

    if (!normalizedName || alreadyExists) {
      return;
    }

    this.addTag.emit({ name: normalizedName, tagType });
    this.cancelAddTag();
  }

  private mapTags(
    tags: Array<{ id?: number; name: string; fromSystem?: boolean; tagType: string }>
  ): TagDTO[] {
    return tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      fromSystem: tag.fromSystem,
      tagType: tag.tagType === "INTERNAL" ? "INTERNAL" : "PUBLIC"
    }));
  }

  private mapSuggestions(response: unknown): TagDTO[] {
    const tags = Array.isArray(response)
      ? response
      : Array.isArray((response as { content?: unknown[] })?.content)
        ? (response as { content: unknown[] }).content
        : [];

    return this.mapTags(
      tags as Array<{
        id?: number;
        name: string;
        fromSystem?: boolean;
        tagType: string;
      }>
    );
  }

  private scheduleClose(): void {
    this.clearCloseTimeout();
    this.closeTimeoutId = setTimeout(() => {
      this.isPopoverOpen = false;
    }, 150);
  }

  private clearCloseTimeout(): void {
    if (!this.closeTimeoutId) {
      return;
    }

    clearTimeout(this.closeTimeoutId);
    this.closeTimeoutId = undefined;
  }
}
